import { concatBytes, ownBytes, toBase64Url, utf8 } from './encoding';

/** An uncompressed P-256 public key in WebCrypto's raw format: 0x04, then X and Y. */
export const P256_PUBLIC_KEY_BYTES = 65;

/** An ECDSA P-256 signature in WebCrypto's format: r and s, 32 bytes each. */
export const SIGNATURE_BYTES = 64;

/** A device ID is the base64url SHA-256 fingerprint of the device's two public keys. */
export const DEVICE_ID_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/** Active devices one account may register at once, such as a phone, a tablet and a laptop. */
export const MAX_ACTIVE_DEVICES_PER_ACCOUNT = 5;

function checkPublicKey(key: Uint8Array, name: string): void {
  if (key.length !== P256_PUBLIC_KEY_BYTES || key[0] !== 0x04) {
    throw new Error(`${name} must be an uncompressed P-256 public key`);
  }
}

/**
 * The device ID: SHA-256 over a domain label and both raw public keys, the
 * ECDH encryption key first. Anyone can recompute it from the keys, so a
 * fingerprint shown as a QR code confirms both keys at once.
 */
export async function deviceFingerprint(encryptionKey: Uint8Array, signingKey: Uint8Array): Promise<string> {
  checkPublicKey(encryptionKey, 'The encryption key');
  checkPublicKey(signingKey, 'The signing key');
  const input = concatBytes(utf8('curentia/v1/device-fingerprint'), new Uint8Array([0]), encryptionKey, signingKey);
  return toBase64Url(new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', ownBytes(input))));
}

/** What a device signs when it registers, to prove it holds the signing key for this account. */
export function deviceRegistrationBytes(accountId: string, deviceId: string): Uint8Array<ArrayBuffer> {
  return utf8(`curentia/v1/device-registration\0${accountId}\0${deviceId}`);
}
